import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Tooltip,
  Chip,
  GlobalStyles
} from '@mui/material';
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { Gantt, ViewMode } from 'gantt-task-react';
import 'gantt-task-react/dist/index.css';
import { getTimelineTaskTree, updateTimelineTask, updateTimelineTaskCostSheet } from '../../services/api';
import { formatDateEuropean } from '../../utils/dateUtils';
import { useTranslation } from 'react-i18next';

// Task list column width (must match listCellWidth passed to Gantt)
const LIST_CELL_WIDTH = 220;

// Truncate name so it fits inside the bar (library shows text outside bar when it doesn't fit)
const BAR_NAME_MAX_LEN = 22;
const truncateForBar = (name, maxLen = BAR_NAME_MAX_LEN) => {
  if (!name || typeof name !== 'string') return name || '';
  if (name.length <= maxLen) return name;
  return name.slice(0, maxLen - 3) + '…';
};

// Custom task list header: only "Name" column (no From/To columns)
const TaskListHeaderNameOnly = ({ headerHeight, rowWidth, fontFamily }) => (
  <div
    style={{
      display: 'table',
      borderBottom: '1px solid #e6e4e4',
      borderTop: '1px solid #e6e4e4',
      borderLeft: '1px solid #e6e4e4',
      fontFamily,
      fontSize: '14px',
    }}
  >
    <div style={{ display: 'table-row', height: headerHeight - 2 }}>
      <div
        style={{
          display: 'table-cell',
          verticalAlign: 'middle',
          minWidth: rowWidth,
        }}
      >
        &nbsp;Name
      </div>
    </div>
  </div>
);

// Custom task list table: only name column
const TaskListTableNameOnly = ({
  rowHeight,
  rowWidth,
  fontFamily,
  fontSize,
  tasks,
  selectedTaskId,
  setSelectedTask,
  onExpanderClick,
}) => (
  <div
    style={{
      display: 'table',
      borderBottom: '1px solid #e6e4e4',
      borderLeft: '1px solid #e6e4e4',
      fontFamily,
      fontSize,
    }}
  >
    {tasks.map((t, index) => {
      const expanderSymbol = t.hideChildren === false ? '▼' : t.hideChildren === true ? '▶' : '';
      return (
        <div
          key={t.id + 'row'}
          style={{
            display: 'table-row',
            height: rowHeight,
            textOverflow: 'ellipsis',
            backgroundColor: index % 2 === 1 ? '#f5f5f5' : undefined,
          }}
          onClick={() => setSelectedTask(t.id)}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setSelectedTask(t.id)}
          role="button"
          tabIndex={0}
        >
          <div
            style={{
              display: 'table-cell',
              verticalAlign: 'middle',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: rowWidth,
              maxWidth: rowWidth,
            }}
            title={t.originalCostSheet?.name ?? t.name}
          >
            <div style={{ display: 'flex' }}>
              <div
                style={{
                  color: 'rgb(86 86 86)',
                  fontSize: '12px',
                  padding: '0.15rem 0.2rem 0 0.2rem',
                  userSelect: 'none',
                  cursor: expanderSymbol ? 'pointer' : 'default',
                }}
                onClick={(e) => { e.stopPropagation(); onExpanderClick(t); }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onExpanderClick(t))}
              >
                {expanderSymbol || '\u00A0'}
              </div>
              <div style={{ fontSize: '13px', paddingLeft: '1rem', userSelect: 'none' }}>
                {t.originalCostSheet?.name ?? t.name}
              </div>
            </div>
          </div>
        </div>
      );
    })}
  </div>
);

const GanttChart = ({ timelineId, onEditTask, onDeleteTask, onRefresh, scrollContainerRef, viewMode: timelineViewMode = 'general' }) => {
  const { t, i18n } = useTranslation();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState(ViewMode.Day);
  const [timelineData, setTimelineData] = useState(null);
  const scrollPositionRef = useRef({ scrollLeft: 0, scrollTop: 0 });
  const updateTimeoutRef = useRef(null);
  const pendingUpdateRef = useRef(null);
  const pendingCostSheetUpdateRef = useRef(null);
  const dragScrollRef = useRef({ active: false, startX: 0, startScrollLeft: 0 });

  // Click-and-drag to scroll horizontally when clicking on timeline chart (not on task bars).
  // The gantt-task-react library uses an *internal* scroll container for the chart; we must scroll that element.
  const getHorizontalScrollTarget = (root) => {
    if (!root || typeof root.querySelectorAll !== 'function') return null;
    for (const el of root.querySelectorAll('*')) {
      const style = window.getComputedStyle(el);
      const canScrollX = style.overflowX === 'auto' || style.overflowX === 'scroll' || style.overflow === 'auto';
      if (canScrollX && el.scrollWidth > el.clientWidth) return el;
    }
    return root.scrollWidth > root.clientWidth ? root : null;
  };

  useEffect(() => {
    const container = scrollContainerRef?.current;
    if (!container) return;

    const isChartArea = (clientX) => {
      const rect = container.getBoundingClientRect();
      return clientX - rect.left > LIST_CELL_WIDTH;
    };
    const isOnBar = (target) => {
      if (!target || !target.closest) return false;
      return target.closest('.bar') || target.closest('[class*="bar"]');
    };

    const onMouseDown = (e) => {
      if (e.button !== 0) return; // only left click
      if (!isChartArea(e.clientX)) return;
      if (isOnBar(e.target)) return;
      const scrollTarget = getHorizontalScrollTarget(container);
      if (!scrollTarget) return;
      e.preventDefault();
      dragScrollRef.current = {
        active: true,
        startX: e.clientX,
        startScrollLeft: scrollTarget.scrollLeft,
        scrollTarget,
      };
      container.style.cursor = 'grabbing';
      container.style.userSelect = 'none';
    };

    const onMouseMove = (e) => {
      if (!dragScrollRef.current.active || !dragScrollRef.current.scrollTarget) return;
      e.preventDefault();
      const { startX, startScrollLeft, scrollTarget } = dragScrollRef.current;
      const dx = startX - e.clientX;
      const maxScroll = scrollTarget.scrollWidth - scrollTarget.clientWidth;
      scrollTarget.scrollLeft = Math.max(0, Math.min(maxScroll, startScrollLeft + dx));
    };

    const onMouseUp = () => {
      if (!dragScrollRef.current.active) return;
      dragScrollRef.current.active = false;
      dragScrollRef.current.scrollTarget = null;
      container.style.cursor = '';
      container.style.userSelect = '';
    };

    container.addEventListener('mousedown', onMouseDown);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      container.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [scrollContainerRef]);

  useEffect(() => {
    if (timelineId) {
      loadTimelineData();
    }
    
    // Cleanup timeout on unmount
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [timelineId]);

  // When viewMode changes, recalculate tasks from current timelineData (switch General <-> Detailed)
  useEffect(() => {
    if (timelineData && timelineId) {
      setTasks(convertToGanttTasks(timelineData, timelineViewMode));
    }
  }, [timelineViewMode]);

  const loadTimelineData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getTimelineTaskTree(timelineId);
      const data = response.data;
      
      console.log('Timeline data loaded:', data);
      
      // Store timeline data for later use
      setTimelineData(data);
      
      // Convert timeline data to Gantt tasks (viewMode determines if cost sheets are shown as child bars)
      const ganttTasks = convertToGanttTasks(data, timelineViewMode);
      console.log('Gantt tasks created:', ganttTasks);
      setTasks(ganttTasks);
    } catch (err) {
      console.error('Error loading timeline data:', err);
      setError(t('gantt.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  // Recalculate parent dates without full reload - updates tasks in place
  const recalculateParentDates = (updatedTaskId, newStartDate, newEndDate) => {
    if (!timelineData) return;

    // Create a deep copy of timelineData to update
    const updatedTimelineData = JSON.parse(JSON.stringify(timelineData));
    const taskIdNum = parseInt(updatedTaskId.replace('task-', ''));
    let taskFloor = null;
    let taskGroup = null;
    let taskPhase = null;

    // Find and update the task in the copied data
    for (const phase of updatedTimelineData.phases || []) {
      for (const group of phase.groups || []) {
        for (const floor of group.floors || []) {
          const task = floor.tasks?.find(t => t.id === taskIdNum);
          if (task) {
            // Update task dates
            task.startDate = newStartDate.toISOString();
            task.endDate = newEndDate.toISOString();
            taskFloor = floor;
            taskGroup = group;
            taskPhase = phase;
            break;
          }
        }
        if (taskFloor) break;
      }
      if (taskFloor) break;
    }

    if (!taskFloor) return;

    // Recalculate dates for floor, group, and phase
    const floorTaskDates = (taskFloor.tasks || [])
      .filter(t => t.startDate && t.endDate)
      .map(t => ({
        start: new Date(t.startDate),
        end: new Date(t.endDate)
      }));

    if (floorTaskDates.length > 0) {
      const floorStartDate = getEarliestDate(floorTaskDates.map(d => d.start));
      const floorEndDate = getLatestDate(floorTaskDates.map(d => d.end));

      const groupFloorDates = (taskGroup.floors || [])
        .map(floor => {
          const dates = (floor.tasks || [])
            .filter(t => t.startDate && t.endDate)
            .map(t => ({
              start: new Date(t.startDate),
              end: new Date(t.endDate)
            }));
          if (dates.length === 0) return null;
          return {
            start: getEarliestDate(dates.map(d => d.start)),
            end: getLatestDate(dates.map(d => d.end))
          };
        })
        .filter(Boolean);

      if (groupFloorDates.length > 0) {
        const groupStartDate = getEarliestDate(groupFloorDates.map(d => d.start));
        const groupEndDate = getLatestDate(groupFloorDates.map(d => d.end));

        const phaseGroupDates = (taskPhase.groups || [])
          .map(group => {
            const dates = (group.floors || [])
              .flatMap(floor => (floor.tasks || [])
                .filter(t => t.startDate && t.endDate)
                .map(t => ({
                  start: new Date(t.startDate),
                  end: new Date(t.endDate)
                })));
            if (dates.length === 0) return null;
            return {
              start: getEarliestDate(dates.map(d => d.start)),
              end: getLatestDate(dates.map(d => d.end))
            };
          })
          .filter(Boolean);

        if (phaseGroupDates.length > 0) {
          const phaseStartDate = getEarliestDate(phaseGroupDates.map(d => d.start));
          const phaseEndDate = getLatestDate(phaseGroupDates.map(d => d.end));

          // Update timelineData state
          setTimelineData(updatedTimelineData);
          
          // Update tasks in place - only update the changed task and its parents
          setTasks(prevTasks => {
            const hideChildrenMap = new Map();
            prevTasks.forEach(t => {
              if (t.hideChildren !== undefined) {
                hideChildrenMap.set(t.id, t.hideChildren);
              }
            });

            // Update only the changed task and its parents, keep others as-is
            const updatedTasks = prevTasks.map(t => {
              // Preserve hideChildren state
              const hideChildren = hideChildrenMap.get(t.id) ?? t.hideChildren;
              
              if (t.id === updatedTaskId) {
                // Update the changed task - only if dates actually changed
                const newStart = snapToWorkDay(newStartDate, true);
                const newEnd = snapToWorkDay(newEndDate, false);
                if (t.start.getTime() === newStart.getTime() && t.end.getTime() === newEnd.getTime()) {
                  return t; // No change, return same reference
                }
                return {
                  ...t,
                  start: newStart,
                  end: newEnd,
                  hideChildren
                };
              } else if (t.id === `floor-${taskFloor.id}`) {
                // Update floor - only if dates changed
                const newStart = snapToWorkDay(floorStartDate, true);
                const newEnd = snapToWorkDay(floorEndDate, false);
                if (t.start.getTime() === newStart.getTime() && t.end.getTime() === newEnd.getTime()) {
                  return t; // No change, return same reference
                }
                return {
                  ...t,
                  start: newStart,
                  end: newEnd,
                  hideChildren
                };
              } else if (t.id === `group-${taskGroup.id}`) {
                // Update group - only if dates changed
                const newStart = snapToWorkDay(groupStartDate, true);
                const newEnd = snapToWorkDay(groupEndDate, false);
                if (t.start.getTime() === newStart.getTime() && t.end.getTime() === newEnd.getTime()) {
                  return t; // No change, return same reference
                }
                return {
                  ...t,
                  start: newStart,
                  end: newEnd,
                  hideChildren
                };
              } else if (t.id === `phase-${taskPhase.id}`) {
                // Update phase - only if dates changed
                const newStart = snapToWorkDay(phaseStartDate, true);
                const newEnd = snapToWorkDay(phaseEndDate, false);
                if (t.start.getTime() === newStart.getTime() && t.end.getTime() === newEnd.getTime()) {
                  return t; // No change, return same reference
                }
                return {
                  ...t,
                  start: newStart,
                  end: newEnd,
                  hideChildren
                };
              }
              
              // Keep other tasks unchanged - return same reference if hideChildren unchanged
              if (t.hideChildren === hideChildren) {
                return t; // Same reference
              }
              return { ...t, hideChildren };
            });
            
            return updatedTasks;
          });
        }
      }
    }
  };

  // Helper function to snap date to full day (8 AM start, 4 PM end)
  const snapToWorkDay = (date, isStart = true) => {
    const snapped = new Date(date);
    snapped.setHours(isStart ? 8 : 16, isStart ? 0 : 0, 0, 0);
    return snapped;
  };

  // Helper function to get the earliest date from an array of dates
  const getEarliestDate = (dates) => {
    if (!dates || dates.length === 0) return null;
    return dates.reduce((earliest, date) => {
      if (!date) return earliest;
      if (!earliest) return date;
      return date < earliest ? date : earliest;
    }, null);
  };

  // Helper function to get the latest date from an array of dates
  const getLatestDate = (dates) => {
    if (!dates || dates.length === 0) return null;
    return dates.reduce((latest, date) => {
      if (!date) return latest;
      if (!latest) return date;
      return date > latest ? date : latest;
    }, null);
  };

  const convertToGanttTasks = (timelineData, viewMode = 'general') => {
    const tasks = [];
    
    // Get timeline start date or use today as fallback
    const timelineStartDate = timelineData.startDate 
      ? new Date(timelineData.startDate)
      : new Date();
    
    // Ensure timeline start date is at 8 AM
    timelineStartDate.setHours(8, 0, 0, 0);

    const isDetailedView = viewMode === 'detailed';

    // Build tasks in hierarchical order: Phase -> Groups -> Floors -> Tasks (-> Cost sheets in detailed view)
    timelineData.phases?.forEach((phase, phaseIndex) => {
      const groupDates = [];

      // Add phase first (will be updated with dates after processing children)
      const phaseTask = {
        id: `phase-${phase.id}`,
        name: phase.name,
        type: 'project',
        progress: 0,
        hideChildren: false,
        styles: {
          progressColor: '#1976d2',
          progressSelectedColor: '#1565c0',
          backgroundColor: '#e3f2fd',
          backgroundSelectedColor: '#bbdefb'
        }
      };
      tasks.push(phaseTask);

      phase.groups?.forEach((group, groupIndex) => {
        const floorDates = [];

        // Add group (will be updated with dates after processing children)
        const groupTask = {
          id: `group-${group.id}`,
          name: group.name,
          type: 'project',
          progress: 0,
          hideChildren: false,
          project: `phase-${phase.id}`,
          styles: {
            progressColor: '#7b1fa2',
            progressSelectedColor: '#6a1b9a',
            backgroundColor: '#f3e5f5',
            backgroundSelectedColor: '#e1bee7'
          }
        };
        tasks.push(groupTask);

        group.floors?.forEach((floor, floorIndex) => {
          const taskDates = [];

          // Add floor (will be updated with dates after processing children)
          const floorTask = {
            id: `floor-${floor.id}`,
            name: `${floor.name} (${floor.area}m²)`,
            type: 'project',
            progress: 0,
            hideChildren: false,
            project: `group-${group.id}`,
            styles: {
              progressColor: '#388e3c',
              progressSelectedColor: '#2e7d32',
              backgroundColor: '#e8f5e8',
              backgroundSelectedColor: '#c8e6c9'
            }
          };
          tasks.push(floorTask);

          // Process tasks
          floor.tasks?.forEach((task) => {
            let taskStartDate, taskEndDate;
            
            if (task.startDate && task.endDate) {
              // Use saved dates from database
              taskStartDate = snapToWorkDay(new Date(task.startDate), true);
              taskEndDate = snapToWorkDay(new Date(task.endDate), false);
            } else {
              // Calculate default dates based on timeline start and duration
              taskStartDate = new Date(timelineStartDate);
              taskStartDate.setDate(timelineStartDate.getDate() + (phaseIndex * 30) + (groupIndex * 10) + (floorIndex * 5));
              taskStartDate = snapToWorkDay(taskStartDate, true);
              
              taskEndDate = new Date(taskStartDate);
              taskEndDate.setDate(taskStartDate.getDate() + (task.duration || 1));
              taskEndDate = snapToWorkDay(taskEndDate, false);
            }

            taskDates.push({ start: taskStartDate, end: taskEndDate });

            // Calculate dependencies
            let dependencies = [];
            if (task.dependencyTaskId) {
              dependencies.push({
                dependentTask: `task-${task.dependencyTaskId}`,
                type: task.dependencyType || 'finish-to-start'
              });
            }

            // Add task immediately after its floor (in detailed view, tasks with cost sheets get expander)
            const hasCostSheetsInView = isDetailedView && task.costSheets?.length;
            tasks.push({
              start: taskStartDate,
              end: taskEndDate,
              name: task.name,
              id: `task-${task.id}`,
              type: 'task',
              progress: 0,
              project: `floor-${floor.id}`,
              dependencies: dependencies,
              hideChildren: hasCostSheetsInView ? false : undefined,
              styles: {
                progressColor: '#f57c00',
                progressSelectedColor: '#ef6c00',
                backgroundColor: '#fff3e0',
                backgroundSelectedColor: '#ffe0b2'
              },
              // Store original task data for updates
              originalTask: task
            });

            // In detailed view: add cost sheet bars as children (use link start/end when saved, else task dates)
            if (isDetailedView && task.costSheets?.length) {
              task.costSheets.forEach((link) => {
                const cs = link?.costSheet;
                if (!cs?.name) return;
                const csStart = link.startDate && link.endDate
                  ? snapToWorkDay(new Date(link.startDate), true)
                  : taskStartDate;
                const csEnd = link.startDate && link.endDate
                  ? snapToWorkDay(new Date(link.endDate), false)
                  : taskEndDate;
                tasks.push({
                  start: csStart,
                  end: csEnd,
                  name: truncateForBar(cs.name),
                  id: `costsheet-${task.id}-${cs.id}`,
                  type: 'task',
                  progress: 0,
                  project: `task-${task.id}`,
                  dependencies: [],
                  styles: {
                    progressColor: '#5c6bc0',
                    progressSelectedColor: '#3949ab',
                    backgroundColor: '#e8eaf6',
                    backgroundSelectedColor: '#c5cae9'
                  },
                  isCostSheet: true,
                  originalTask: task,
                  originalCostSheet: cs
                });
              });
            }
          });

          // Calculate floor dates from tasks and update floor task
          let floorStartDate, floorEndDate;
          if (taskDates.length > 0) {
            floorStartDate = getEarliestDate(taskDates.map(d => d.start));
            floorEndDate = getLatestDate(taskDates.map(d => d.end));
          } else {
            // No tasks yet, use default dates
            floorStartDate = new Date(timelineStartDate);
            floorStartDate.setDate(timelineStartDate.getDate() + (phaseIndex * 30) + (groupIndex * 10) + (floorIndex * 5));
            floorEndDate = new Date(floorStartDate);
            floorEndDate.setDate(floorStartDate.getDate() + 10);
          }
          
          floorTask.start = snapToWorkDay(floorStartDate, true);
          floorTask.end = snapToWorkDay(floorEndDate, false);
          floorDates.push({ start: floorStartDate, end: floorEndDate });
        });

        // Calculate group dates from floors and update group task
        let groupStartDate, groupEndDate;
        if (floorDates.length > 0) {
          groupStartDate = getEarliestDate(floorDates.map(d => d.start));
          groupEndDate = getLatestDate(floorDates.map(d => d.end));
        } else {
          // No floors yet, use default dates
          groupStartDate = new Date(timelineStartDate);
          groupStartDate.setDate(timelineStartDate.getDate() + (phaseIndex * 30) + (groupIndex * 10));
          groupEndDate = new Date(groupStartDate);
          groupEndDate.setDate(groupStartDate.getDate() + 20);
        }
        
        groupTask.start = snapToWorkDay(groupStartDate, true);
        groupTask.end = snapToWorkDay(groupEndDate, false);
        groupDates.push({ start: groupStartDate, end: groupEndDate });
      });

      // Calculate phase dates from groups and update phase task
      let phaseStartDate, phaseEndDate;
      if (groupDates.length > 0) {
        phaseStartDate = getEarliestDate(groupDates.map(d => d.start));
        phaseEndDate = getLatestDate(groupDates.map(d => d.end));
      } else {
        // No groups yet, use default dates
        phaseStartDate = new Date(timelineStartDate);
        phaseStartDate.setDate(timelineStartDate.getDate() + (phaseIndex * 30));
        phaseEndDate = new Date(phaseStartDate);
        phaseEndDate.setDate(phaseStartDate.getDate() + 30);
      }

      phaseTask.start = snapToWorkDay(phaseStartDate, true);
      phaseTask.end = snapToWorkDay(phaseEndDate, false);
    });

    return tasks;
  };

  const handleDateChange = async (task, children) => {
    const snappedStart = snapToWorkDay(new Date(task.start), true);
    const snappedEnd = snapToWorkDay(new Date(task.end), false);

    // --- Cost sheet bar dragged: save this cost sheet's dates and parent task = span of all cost sheet children ---
    if (task.id.startsWith('costsheet-')) {
      const parentTaskId = task.project; // e.g. 'task-123'
      const [, taskIdStr, costSheetIdStr] = task.id.match(/^costsheet-(\d+)-(\d+)$/) || [];
      const costSheetTaskId = taskIdStr ? parseInt(taskIdStr, 10) : null;
      const costSheetId = costSheetIdStr ? parseInt(costSheetIdStr, 10) : null;
      pendingCostSheetUpdateRef.current = null;

      setTasks(prevTasks => {
        const updated = prevTasks.map(t =>
          t.id === task.id ? { ...t, start: snappedStart, end: snappedEnd } : t
        );
        const costSheetSiblings = updated.filter(t => t.project === parentTaskId);
        if (costSheetSiblings.length === 0) return updated;
        const parentStart = getEarliestDate(costSheetSiblings.map(t => t.start));
        const parentEnd = getLatestDate(costSheetSiblings.map(t => t.end));
        if (!parentStart || !parentEnd) return updated;
        const parentStartSnap = snapToWorkDay(parentStart, true);
        const parentEndSnap = snapToWorkDay(parentEnd, false);
        pendingCostSheetUpdateRef.current = {
          parentTaskId,
          parentStartSnap,
          parentEndSnap,
          costSheetTaskId,
          costSheetId,
          costSheetStart: snappedStart,
          costSheetEnd: snappedEnd
        };
        return updated.map(t =>
          t.id === parentTaskId
            ? { ...t, start: parentStartSnap, end: parentEndSnap }
            : t
        );
      });

      if (updateTimeoutRef.current) clearTimeout(updateTimeoutRef.current);
      updateTimeoutRef.current = setTimeout(() => {
        const pending = pendingCostSheetUpdateRef.current;
        pendingCostSheetUpdateRef.current = null;
        if (!pending) return;
        const { parentTaskId: pid, parentStartSnap: pStart, parentEndSnap: pEnd, costSheetTaskId: csTaskId, costSheetId: csId, costSheetStart: csStart, costSheetEnd: csEnd } = pending;
        try {
          recalculateParentDates(pid, pStart, pEnd);
          const parentTaskIdNum = parseInt(pid.replace('task-', ''), 10);
          updateTimelineTask(parentTaskIdNum, {
            startDate: pStart.toISOString(),
            endDate: pEnd.toISOString(),
            duration: Math.ceil((pEnd.getTime() - pStart.getTime()) / (1000 * 60 * 60 * 24))
          }).catch(err => {
            console.error('Error updating task date:', err);
            loadTimelineData();
          });
          if (csTaskId != null && csId != null) {
            updateTimelineTaskCostSheet(csTaskId, csId, {
              startDate: csStart.toISOString(),
              endDate: csEnd.toISOString()
            }).catch(err => {
              console.error('Error updating cost sheet link dates:', err);
              loadTimelineData();
            });
          }
        } catch (err) {
          console.error('Error recalculating parent dates:', err);
          setError(t('gantt.failedToUpdateDate'));
        }
        updateTimeoutRef.current = null;
      }, 500);
      return;
    }

    // Only handle date changes for actual tasks (not parent items like phase/group/floor)
    if (task.type !== 'task' || !task.originalTask) {
      return;
    }

    // --- Normal task dragged: in detailed view, push new dates to all cost sheet children ---
    setTasks(prevTasks => {
      const hasCostSheetChildren = timelineViewMode === 'detailed' && prevTasks.some(t => t.project === task.id);
      return prevTasks.map(t => {
        if (t.id === task.id) {
          return { ...t, start: snappedStart, end: snappedEnd };
        }
        if (hasCostSheetChildren && t.project === task.id) {
          return { ...t, start: snappedStart, end: snappedEnd };
        }
        return t;
      });
    });

    const costSheetIds = (task.originalTask?.costSheets || []).map((link) => link?.costSheet?.id).filter((id) => id != null);
    pendingUpdateRef.current = {
      taskId: task.id,
      originalTaskId: task.originalTask.id,
      snappedStart,
      snappedEnd,
      durationDays: Math.ceil((snappedEnd.getTime() - snappedStart.getTime()) / (1000 * 60 * 60 * 24)),
      costSheetIds
    };

    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }

    updateTimeoutRef.current = setTimeout(() => {
      const update = pendingUpdateRef.current;
      if (!update) return;

      try {
        recalculateParentDates(update.taskId, update.snappedStart, update.snappedEnd);

        updateTimelineTask(update.originalTaskId, {
          startDate: update.snappedStart.toISOString(),
          endDate: update.snappedEnd.toISOString(),
          duration: update.durationDays
        }).catch(error => {
          console.error('Error updating task date:', error);
          loadTimelineData();
        });

        // In detailed view, persist each cost sheet link's start/end to match the task
        if (Array.isArray(update.costSheetIds) && update.costSheetIds.length > 0) {
          update.costSheetIds.forEach((costSheetId) => {
            updateTimelineTaskCostSheet(update.originalTaskId, costSheetId, {
              startDate: update.snappedStart.toISOString(),
              endDate: update.snappedEnd.toISOString()
            }).catch((err) => {
              console.error('Error updating cost sheet link dates:', err);
              loadTimelineData();
            });
          });
        }

        pendingUpdateRef.current = null;
      } catch (error) {
        console.error('Error updating task date:', error);
        setError(t('gantt.failedToUpdateDate'));
      }
    }, 500);
  };

  const handleTaskClick = (task) => {
    if (task.type === 'task') {
      onEditTask?.(task);
    }
  };

  const handleTaskDelete = (task) => {
    if (task.type === 'task') {
      onDeleteTask?.(task);
    }
  };

  const handleExpanderClick = (task) => {
    // Toggle collapse/expand for project/group/floor rows
    setTasks((prev) =>
      prev.map((t) =>
        t.id === task.id ? { ...t, hideChildren: !t.hideChildren } : t
      )
    );
  };

  const handleZoomIn = () => {
    if (viewMode === ViewMode.Year) {
      setViewMode(ViewMode.Month);
    } else if (viewMode === ViewMode.Month) {
      setViewMode(ViewMode.Week);
    } else if (viewMode === ViewMode.Week) {
      setViewMode(ViewMode.Day);
    }
  };

  const handleZoomOut = () => {
    if (viewMode === ViewMode.Day) {
      setViewMode(ViewMode.Week);
    } else if (viewMode === ViewMode.Week) {
      setViewMode(ViewMode.Month);
    } else if (viewMode === ViewMode.Month) {
      setViewMode(ViewMode.Year);
    }
  };

  const getViewModeLabel = () => {
    switch (viewMode) {
      case ViewMode.Day:
        return t('gantt.day');
      case ViewMode.Week:
        return t('gantt.week');
      case ViewMode.Month:
        return t('gantt.month');
      case ViewMode.Year:
        return t('gantt.year');
      default:
        return t('gantt.day');
    }
  };

  // Hide children of collapsed parents (e.g. cost sheets when general task is collapsed). Must run before any early return (hooks rules).
  const visibleTasks = useMemo(() => {
    if (!tasks || tasks.length === 0) return [];
    return tasks.filter((t) => {
      let parentId = t.project;
      while (parentId) {
        const parent = tasks.find((p) => p.id === parentId);
        if (!parent) break;
        if (parent.hideChildren === true) return false;
        parentId = parent.project;
      }
      return true;
    });
  }, [tasks]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
        <Button onClick={loadTimelineData} sx={{ ml: 2 }}>
          Retry
        </Button>
      </Alert>
    );
  }

  if (!tasks || tasks.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          {t('gantt.noDataTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('gantt.noDataBody')}
        </Typography>
        <Button 
          variant="outlined" 
          onClick={loadTimelineData}
          sx={{ mt: 2 }}
        >
          {t('gantt.refreshData')}
        </Button>
      </Paper>
    );
  }

  return (
    <Box>
      {/* Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">{t('gantt.projectTimeline')}</Typography>
          <Chip label={getViewModeLabel()} color="primary" variant="outlined" />
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={t('gantt.zoomOut')}>
            <IconButton onClick={handleZoomOut} disabled={viewMode === ViewMode.Year}>
              <ZoomOutIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('gantt.zoomIn')}>
            <IconButton onClick={handleZoomIn} disabled={viewMode === ViewMode.Day}>
              <ZoomInIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('common.refresh')}>
            <IconButton onClick={loadTimelineData}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Gantt Chart */}
      <Paper sx={{ p: 2 }}>
        <GlobalStyles
          styles={{
            /* Make text in bars black - target all SVG text elements */
            'svg text': {
              fill: '#000000',
            },
            '[class*="gantt"] svg text': {
              fill: '#000000',
            },
            'svg g text': {
              fill: '#000000',
            },
            '[class*="gantt"] svg g text': {
              fill: '#000000',
            },
            'g[class*="bar"] text, g[class*="task"] text, g[class*="project"] text': {
              fill: '#000000',
            },
          }}
        />
        <Gantt
          tasks={visibleTasks}
          viewMode={viewMode}
          onDateChange={handleDateChange}
          onTaskClick={handleTaskClick}
          onDelete={handleTaskDelete}
          onExpanderClick={handleExpanderClick}
          listCellWidth={220}
          rowHeight={50}
          columnWidth={65}
          barCornerRadius={2}
          fontFamily="Inter, Roboto, Arial, sans-serif"
          fontSize="12px"
          locale={i18n.language}
          barBackgroundColor="#1976d2"
          barBackgroundSelectedColor="#1565c0"
          barProgressColor="#4caf50"
          barProgressSelectedColor="#388e3c"
          projectBackgroundColor="#e3f2fd"
          projectBackgroundSelectedColor="#bbdefb"
          projectProgressColor="#1976d2"
          projectProgressSelectedColor="#1565c0"
          arrowColor="#1976d2"
          arrowIndent={20}
          todayColor="#ff5722"
          TaskListHeader={TaskListHeaderNameOnly}
          TaskListTable={TaskListTableNameOnly}
          TooltipContent={({ task }) => (
            <div style={{
              padding: '8px',
              backgroundColor: '#fff',
              border: '1px solid #ccc',
              borderRadius: '4px',
              maxWidth: '280px',
              overflowWrap: 'break-word',
              wordBreak: 'break-word',
            }}>
              <div><strong>{task.originalCostSheet?.name ?? task.name}</strong></div>
              {task.isCostSheet && task.originalTask && (
                <div style={{ fontSize: '12px', color: '#555', marginBottom: '4px' }}>
                  {t('gantt.tooltipPartOfTask')}: {task.originalTask.name}
                </div>
              )}
              <div>{t('gantt.tooltipStart')}: {formatDateEuropean(task.start)}</div>
              <div>{t('gantt.tooltipEnd')}: {formatDateEuropean(task.end)}</div>
              <div>{t('gantt.tooltipDuration')}: {Math.ceil((task.end - task.start) / (1000 * 60 * 60 * 24))} {t('gantt.tooltipDays')}</div>
              {task.progress > 0 && <div>{t('gantt.tooltipProgress')}: {task.progress}%</div>}
            </div>
          )}
          key={`gantt-${timelineId}`}
        />
      </Paper>
    </Box>
  );
};

export default GanttChart;
